import {useState, useEffect, useMemo} from 'react';
import ActionBar from '../actionBar';
import {compose} from '../../lib/compose';
import Table from '../table';
import { gql } from "@apollo/client";
import { useQuery } from "@apollo/client/react";
import {useDeviceMutations} from './deviceMutations';
import {firmwareAssignmentFormatter, modelFormatter, fulcrumShield, hdmShield} from './columnFormatters';
import {dateFormatter} from '../dateFormatter';
import {useAtomValue} from 'jotai';
import {pageSizeRecoil, showDeleted} from '../recoil';
import Portal from '../portal';
import {usePortalContext} from '../../lib/context/portalContext';
import {useFeatureContext} from '../../lib/context/featureContext';
import {useCustomerContext} from '../../lib/context/customerContext';
import withAlerts from '../../lib/withAlerts';
import SafeCaseDetails from './safeCaseDetails';
import SubscriptionDetails from './subscriptionDetails';
import UserDetails from '../users/userDetails';
import PolicyAssignment from '../policy/policyAssignment';
import PhoneBonding from './phoneBonding';
import HDMDetails from './hdmDetails';
import WhitelistDetails from './whitelistDetails';
import {Button} from 'react-bootstrap';
import BulkOtaDetails from './bulkOtaDetails';
//import BulkUserAssignment from './bulkUserAssignment';
// NOTE (per user, 2025): bulk user assignment (SAFECASE_BULK_USER_ASSIGN)
// has been superseded by a new multi-purpose bulk-operations form built
// elsewhere in the app - this component/button is paused, not just
// commented out for RBAC reasons. Confirm with the user before deleting
// bulkUserAssignment.jsx outright; not gated or touched in this pass.
import {useSearch, useSort, useFilter} from '../../lib/useQueryModifiers';

export const Devices = props => {
  const {feature_subscriptions, feature_whitelist, feature_phone_bonding} = useFeatureContext();
  const {rowData, togglePortal, isShowing, clicked} = usePortalContext();
  const {hasPermission} = useCustomerContext();
  const {alerts} = props;
  const [search, setSearch] = useSearch();
  const [sort, setSort] = useSort();
  const [pageNum, setPageNum] = useState(1);
  const [totalRecordsState, setTotalRecordsState] = useState(0);
  const showingDeleted = useAtomValue(showDeleted);
  const pageSize = useAtomValue(pageSizeRecoil);

  const devicesFilters = useMemo(() => ({
    combineAnd: [
      ...search,
      ...sort,
      { field: "options.show_deleted", value: `${showingDeleted}` },
      { field: "with.device_state", value: "1" },
      { field: "with.product_type", value: "1" },
      { field: "with.reported_firmware", value: "1" },
      { field: "with.firmware", value: "1" },
      { field: "with.user", value: "1" },
      { field: "with.policy", value: "1" },
      { field: "with.device_configuration", value: "1" },
      { field: "with.hdm_config_preset_profile", value: "1" },
      { field: "with.linked_phones", value: "1" },
      { field: "search.fields", value: `serial_number` },
      { field: "search.fields", value: `user.username` },
      { field: "search.fields", value: `policy.name` },
      { field: "options.page.num", value: `${pageNum}` },
      { field: "options.page.len", value: `${pageSize}` }
    ]
  }), [search, sort, showingDeleted, pageNum, pageSize]);

  const {data,loading} = useQuery(DEVICES, 
    {
      variables: { filters: devicesFilters },
      // devices q polling added for OTA updates
      pollInterval: 8000,
      skip: !sort.length
    });

  const totalQueried = data?.Devices?.metadata?.page?.records ?? totalRecordsState;

  useEffect(()=> {
    setTotalRecordsState(totalQueried);
  }, [totalQueried]);

  useEffect(() => {
    return setPageNum(1)
  }, [search]);

  let devices = data?.Devices?.result ?? [];

  devices = devices.map(device => {
    return { ...device, is_delete: showingDeleted, defaultKey: ""};
  });

  const columns = [
    { dataField: 'serial_number', text: 'SAFECASE SERIAL NO.' },
    { dataField: 'product_type.description', text: 'MODEL', formatter: modelFormatter },
    { dataField: 'reported_firmware.version', text: 'Firmware', formatter: firmwareAssignmentFormatter },
    { dataField: 'user.username', text: 'USER ID' },
    { dataField: 'policy.name', text: 'Policy' },
    { dataField: 'modified_at', text: 'Last Updated (UTC)', sort: true, formatter: dateFormatter }
  ];
  
  return (
    <>
      <Portal title={clicked === 'bulkuserassign' ? "Bulk User Assignment" : "SafeCase"}>
        {rowData?.serial_number && <SafeCaseDetails type="safecase" title="SafeCase"/>}
        {/* RBAC: SAFECASE_USER_ASSIGNMENT no longer hides this whole section -
            SAFECASE_VIEW (already required to reach this off-canvas) covers
            viewing; SAFECASE_USER_ASSIGNMENT now gates only the Assign/
            Unassign controls inside userDetails.jsx itself. */}
        {rowData?.serial_number &&
          <UserDetails type="user" title="User"/>
        }
        {/* RBAC: SAFECASE_POLICY_ASSIGNMENT now gates only the assign/change/
            unassign controls inside policyAssignment.jsx - viewing the
            currently assigned policy's name/rules stays visible under
            SAFECASE_VIEW. */}
        {rowData?.serial_number &&
          <PolicyAssignment type="policy" title="Policy" />
        }
        {/* RBAC: SAFECASE_HDM_ASSIGNMENT now gates only the assign/change
            controls inside hdmDetails.jsx - viewing the currently assigned
            profile/preset stays visible under SAFECASE_VIEW. */}
        {rowData?.product_type?.greyhound_family &&
          <HDMDetails type="hdm" title="HDM Profile"/>
        }
        {/* RBAC: SAFECASE_SSID_ASSIGNMENT now gates only the assign/change/
            unassign controls inside whitelistDetails.jsx - viewing the
            currently assigned list name stays visible under SAFECASE_VIEW. */}
        {feature_whitelist && rowData?.product_type?.whippethusky_family &&
          <WhitelistDetails type="wifi" title="SSID Whitelist" />
        }
        {/* No permission code exists for Subscriptions in the RBAC sheet -
            left feature-flag-only, same as the Subscriptions nav item gap. */}
        {feature_subscriptions && rowData?.product_type?.samsung_family &&  <SubscriptionDetails type="policy" title="Subscriptions"/>}
        {/* RBAC: SAFECASE_UNLINK now gates only the Unlink action inside
            phoneBonding.jsx - viewing the linked phone ID / unlinking-in-
            progress state stays visible under SAFECASE_VIEW, matching the
            same pattern applied to User/Policy/HDM/SSID above. */}
        {feature_phone_bonding && rowData?.product_type?.whippethusky_family &&
          <PhoneBonding type="safecase" title="SafeCase/Phone Linking"/>
        }
        {clicked === 'bulkota' && <BulkOtaDetails type="safecase" title="Update All Firmware" /> }
      </Portal>

      <ActionBar showHidden={false} searchChange={setSearch} title="Manage SafeCases">
        <div>
          {/* RBAC: SAFECASE_BULK_OTA - "Disable the 'Update Firmware for all SafeCases' button." */}
          <Button data-testid="bulk-ota" disabled={!hasPermission('SAFECASE_BULK_OTA')} clicked={'bulkota'} defaultkey={0} onClick={togglePortal}>Update Firmware for all SafeCases</Button>
        </div>
      </ActionBar>
      <Table
        testId="safecases-table"
        className="safecases-table"
        data={devices}
        columns={columns}
        loading={loading}
        pageSize={pageSize}
        pageNum={pageNum}
        setPageNum={setPageNum}
        totalRecords={totalRecordsState}
        setSort={setSort}
      />
    </>
  );
};

const DEVICES = gql`
  query getDevices($filters: GenericFilters!) {
    Devices (filters: $filters) {
      result {
        id
        policy_id
        device_model_id
        serial_number
        mcu_id
        is_active
        ota_state
        ota_progress
        modified_at
        whitelist_ssid_list_id
        linked_phones { linked_phone_id }
        hdm_config_preset_profile_id
        hdm_config_preset_profile { name id }
        device_state { hdm_preset_option }
        device_configuration {
          needs_hdm_unlink
          is_hdm_enabled
          is_hdm_enabled_expiry
          is_hdm_enabled_end_date
          is_anti_surveillance_enabled
          is_anti_surveillance_enabled_expiry
          is_anti_surveillance_enabled_end_date
        }
        user { id username firstname lastname email phone is_active modified_at }
        policy {
          id name description is_active is_delete is_phone_protected is_offline
          has_battery_warning battery_warning_threshold is_checked_in is_phone_mismatch
          is_camera_exposed is_cellular_connected is_microphone_exposed is_phone_present
          policy_action_id created_at modified_at
        }
        product_type_id
        product_type { id description greyhound_family samsung_family whippethusky_family }
        firmware_id
        firmware { id version description is_active }
        reported_firmware_id
        reported_firmware { id version description }
      }
      metadata { page { num len total inPage records } }
    }
  }
`;

export default compose(withAlerts)(Devices);
